<template>
  <div class="app-container">
    <div class="main-content">
      <!-- 左侧文本输入区 -->
      <div class="text-input-section">
        <div class="input-header">
          <span>请输入文本</span>
          <div class="limit-info">
            <span class="char-count">还可输入{{ 3000 - inputText.length }}字符</span>
            <span class="limit-tip">每天最多使用3000个字符</span>
          </div>
        </div>
        
        <el-input
          type="textarea"
          v-model="inputText"
          :maxlength="3000"
          :rows="12"
          placeholder="一次最多输入3000个字符"
        ></el-input>
      </div>

      <!-- 右侧控制区 -->
      <div class="control-section">
        <div class="language-select">
          <span>请选择朗读语言</span>
          <select v-model="language" class="language-dropdown">
            <option value="cmn-CN">中文</option>
            <option value="en-US">英语</option>
            <option value="ja-JP">日语</option>
            <option value="es-ES">西班牙语</option>
            <option value="fr-FR">法语</option>
            <option value="de-DE">德语</option>
            <option value="it-IT">意大利语</option>
            <option value="pt-BR">葡萄牙语</option>
            <option value="pl-PL">波兰语</option>
            <option value="ru-RU">俄语</option>
            <option value="nl-NL">荷兰语</option>
            <option value="ko-KR">韩语</option>
            <option value="tr-TR">土耳其语</option>
            <option value="da-DK">丹麦语</option>
            <option value="arb">阿拉伯语</option>
            <option value="cs-CZ">捷克语</option>
            <option value="fi-FI">芬兰语</option>
            <option value="hi-IN">印地语</option>
            <option value="is-IS">冰岛语</option>
            <option value="nb-NO">挪威语</option>
            <option value="sv-SE">瑞典语</option>
            <option value="cy-GB">威尔士语</option>
            <option value="ro-RO">罗马尼亚语</option>
            <option value="ca-ES">加泰罗尼亚语</option>
          </select>
        </div>

        <el-button 
          type="primary" 
          class="convert-btn" 
          style="width: 100%" 
          @click="convertToSpeech"
          :loading="isConverting"
        >
          开始转换
        </el-button>

        <div class="action-buttons">
          <el-button @click="playAudio" :disabled="!audioUrl">试听</el-button>
          <el-button @click="downloadAudio" :disabled="!audioUrl">mp3下载</el-button>
        </div>
      </div>
    </div>

    <!-- 音频播放器（隐藏） -->
    <audio ref="audioPlayer" :src="audioUrl"></audio>

    <!-- 底部说明文本 -->
    <div class="description">
      okreader是一款免费的文本转语音工具，提供语音合成服务，支持多种语言，包括中文、英语、日语、韩语、法语、德语、西班牙语、阿拉伯语等二十多种语言。
      <br><br>
      <span class="tips-title">温馨提示</span>：本站已接入网络备案，依法记录相关信息，请勿转换违规违法内容，任何违规行为后果自负。转换服务由AI技术（语音合成类算法）生成，用户拥有100%可商用权利（支持商用场景），用户转换生成的内容不代表okreader的态度或观点。
      <br><br>
  <a href="https://beian.miit.gov.cn/#/Integrated/index" target="_blank" class="beian-link">
    备案号：粤ICP备2022052608号
  </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      inputText: '',
      language: 'cmn-CN',
      audioUrl: '',
      isConverting: false
    }
  },
  methods: {
    async convertToSpeech() {
      if (!this.inputText) {
        this.$message.warning('请输入要转换的文本');
        return;
      }

      this.isConverting = true;

      try {
        // 处理文本，添加 SSML 标签
        const processedText = `<speak>${this.inputText
          .split('.')
          .filter(s => s.trim())
          .join('.<break time="1s"/>')}</speak>`;
          
           console.log('发送请求:', {
            text: processedText,
            language: this.language,
            TextType: 'ssml',
            Engine: 'standard',
            VoiceId: this.language === 'cmn-CN' ? 'Zhiyu' : 'Joanna'
        });

        const response = await fetch('https://okreader.cn/api/synthesize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: processedText,
            language: this.language,
            TextType: 'ssml',
            Engine: 'standard',
            VoiceId: this.language === 'cmn-CN' ? 'Zhiyu' : 'Joanna'
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '转换失败');
        }

        const blob = await response.blob();
        this.audioUrl = URL.createObjectURL(blob);
        this.$message.success('转换成功');
      } catch (error) {
        console.error('转换失败:', error);
        this.$message.error(error.message || '转换失败，请重试');
      } finally {
        this.isConverting = false;
      }
    },

    playAudio() {
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.play();
      }
    },

    downloadAudio() {
      if (this.audioUrl) {
        const link = document.createElement('a');
        link.href = this.audioUrl;
        link.download = 'audio.mp3';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }
}
</script>

<style>
.app-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.main-content {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.text-input-section {
  flex: 1;
}

.input-header {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.input-header .limit-info {
  display: flex;
  gap: 4ch;  /* 设置4个字符的间隔 */
  align-items: center;
}

.control-section {
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.language-select {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: space-between;
}

.action-buttons .el-button {
  flex: 1;
  min-width: 120px;
  padding-left: 20px;
  padding-right: 20px;
}

.description {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  text-align: justify;  /* 添加两端对齐 */
  width: 100%;
  max-width: calc(100% - 320px);  /* 减去右侧控制区域的宽度 */
  margin-top: 20px;  /* 添加上边距 */
}

.language-select select.language-dropdown {
  height: 40px;  /* 与 el-button 默认高度一致 */
  padding: 0 15px;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  color: #606266;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  background-color: #FFF;
  width: 100%;
}

.language-select select.language-dropdown:hover {
  border-color: #C0C4CC;
}

.language-select select.language-dropdown:focus {
  border-color: #409EFF;
}

/* 移动端适配 (屏幕宽度小于768px) */
@media screen and (max-width: 768px) {
  .main-content {
    flex-direction: column;
    gap: 15px;
  }

  .control-section {
    width: 100%;
  }

  .input-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .input-header .limit-info {
    width: 100%;
    justify-content: space-between;
  }

  .text-input-section {
    width: 100%;
  }

  .language-select {
    margin-top: 10px;
  }

  .action-buttons {
    margin-top: 10px;
  }

  .description {
    max-width: 100%;
  }

  /* 调整文本框高度 */
  .el-input textarea {
    min-height: 200px;
  }
}

/* 更小屏幕的优化 (屏幕宽度小于480px) */
@media screen and (max-width: 480px) {
  .app-container {
    padding: 15px;
  }

  .action-buttons {
    flex-direction: row;
    gap: 10px;
  }

  .action-buttons .el-button {
    padding: 10px 15px;
    min-width: unset;
  }
}

.tips-title {
  color: #409EFF;  /* Element UI 的主题蓝色 */
}
.beian-link {
  color: #666;
  text-decoration: none;
  display: block;  /* 让链接独占一行 */
  text-align: center;  /* 文字居中 */
  margin-top: 20px;  /* 与上方文字保持间距 */
  padding-bottom: 20px;  /* 底部留出空间 */
}

.beian-link:hover {
  color: #409EFF;
}
</style> 